import { useCallback, useEffect, useRef, useState } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Polyline from '@arcgis/core/geometry/Polyline';
import Point from '@arcgis/core/geometry/Point';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import MapView from '@arcgis/core/views/MapView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import FeatureLayerView from '@arcgis/core/views/layers/FeatureLayerView';
import type { NearestPropertyRow } from './types';

/** Minimal handle type returned by FeatureLayerView.highlight() */
interface HighlightHandle {
  remove(): void;
}

interface UseNearestPropertyHoverLineOptions {
  view: MapView | undefined;
  boundariesLayer: FeatureLayer | undefined;
}

// ── Color palette for "show all lines" mode ────────────────────────────────
// 10 visually distinct colors. Each row is assigned a color by index.
const LINE_PALETTE = [
  '#FF6B35', // orange
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#EF4444', // red
  '#6366F1', // indigo
];

/** Parse hex color to [r, g, b] */
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

const DEFAULT_LINE_SYMBOL = new SimpleLineSymbol({
  color: [255, 120, 50, 0.85],
  width: 2.5,
  style: 'short-dash',
});

/** Build a line symbol with a specific color for "show all" mode */
function makeLineSymbol(hex: string, width = 2, opacity = 0.7): SimpleLineSymbol {
  const [r, g, b] = hexToRgb(hex);
  return new SimpleLineSymbol({
    color: [r, g, b, opacity],
    width,
    style: 'short-dash',
  });
}

/** Build a glow line symbol — wider, brighter version of the row color */
function makeGlowSymbol(hex: string): SimpleLineSymbol {
  const [r, g, b] = hexToRgb(hex);
  return new SimpleLineSymbol({
    color: [r, g, b, 1],
    width: 5,
    style: 'solid',
  });
}

/** Build the distance label text symbol */
function makeDistanceLabel(distanceMiles: number, haloHex?: string): TextSymbol {
  const [hr, hg, hb] = haloHex ? hexToRgb(haloHex) : [0, 0, 0];
  return new TextSymbol({
    text: `${Math.round(distanceMiles)} mi`,
    color: 'white',
    haloColor: haloHex ? [hr, hg, hb, 0.9] as unknown as string : 'black',
    haloSize: '1px',
    xoffset: 3,
    yoffset: 3,
    font: { size: 11, family: 'sans-serif', weight: 'bold' },
  });
}

/** Get a label point at ~25% along the polyline path */
function getLabelPoint(paths: number[][][], sr: SpatialReference): Point | null {
  const startPt = paths[0]?.[0];
  const endPt = paths[0]?.[paths[0].length - 1];
  if (!startPt || !endPt) return null;
  const [x0, y0] = startPt;
  const [x1, y1] = endPt;
  if (x0 == null || y0 == null || x1 == null || y1 == null) return null;
  const t = 0.25;
  return new Point({
    x: x0 + (x1 - x0) * t,
    y: y0 + (y1 - y0) * t,
    spatialReference: sr,
  });
}

/** Assign a lineColor to each row by index. Returns new row objects. */
export function assignLineColors(rows: NearestPropertyRow[]): NearestPropertyRow[] {
  return rows.map((row, i) => ({
    ...row,
    lineColor: LINE_PALETTE[i % LINE_PALETTE.length],
  }));
}

/**
 * Hook that manages drawing dashed lines from the source property to
 * nearest-property rows on the map.
 *
 * Supports two modes:
 *  - **Hover-only** (default): draws a single line + label when a row is hovered.
 *  - **Show all lines**: draws all lines at once with per-row colors.
 *    Hovering a row "glows" its line (wider + full opacity) and dims the others.
 *
 * Returns `handleHoverRow` / `handleSelectRow` handlers for the table,
 * plus `showAllLines` / `toggleShowAllLines` / `setAllLineRows` for the toggle.
 */
export function useNearestPropertyHoverLine({
  view,
  boundariesLayer,
}: UseNearestPropertyHoverLineOptions) {
  // Graphics layer for hover-only lines
  const hoverGfxLayerRef = useRef<GraphicsLayer | null>(null);
  // Graphics layer for "show all" persistent lines
  const allLinesGfxLayerRef = useRef<GraphicsLayer | null>(null);
  const layerViewRef = useRef<FeatureLayerView | null>(null);
  const highlightHandleRef = useRef<HighlightHandle | null>(null);

  const [showAllLines, setShowAllLines] = useState(false);
  // Rows to render when "show all" is active — must have lineColor assigned
  const allLineRowsRef = useRef<NearestPropertyRow[]>([]);

  // Create / attach the graphics layers once per view
  useEffect(() => {
    if (!view) return;

    const hoverLayer = new GraphicsLayer({
      title: 'nearest-prop-hover-line',
      listMode: 'hide',
    });
    const allLayer = new GraphicsLayer({
      title: 'nearest-prop-all-lines',
      listMode: 'hide',
    });
    hoverGfxLayerRef.current = hoverLayer;
    allLinesGfxLayerRef.current = allLayer;
    view.map?.add(allLayer);
    view.map?.add(hoverLayer); // on top

    return () => {
      hoverLayer.removeAll();
      allLayer.removeAll();
      view.map?.remove(hoverLayer);
      view.map?.remove(allLayer);
      hoverLayer.destroy();
      allLayer.destroy();
      hoverGfxLayerRef.current = null;
      allLinesGfxLayerRef.current = null;
    };
  }, [view]);

  // Obtain a FeatureLayerView for independent highlight via layerView.highlight()
  useEffect(() => {
    if (!view || !boundariesLayer) return;

    let cancelled = false;
    view.whenLayerView(boundariesLayer).then((lv) => {
      if (!cancelled) layerViewRef.current = lv as FeatureLayerView;
    });

    return () => {
      cancelled = true;
      highlightHandleRef.current?.remove();
      highlightHandleRef.current = null;
      layerViewRef.current = null;
    };
  }, [view, boundariesLayer]);

  // ── Draw all persistent lines ─────────────────────────────────────────────
  const drawAllLines = useCallback(
    (rows: NearestPropertyRow[]) => {
      const gfx = allLinesGfxLayerRef.current;
      if (!gfx) return;
      gfx.removeAll();

      for (const row of rows) {
        const lg = row.lineGraphic;
        if (!lg || !row.lineColor) continue;

        const sr = SpatialReference.fromJSON(lg.spatialReference);
        const polyline = new Polyline({ paths: lg.paths, spatialReference: sr });

        // Line graphic tagged with objectId so we can find it on hover
        const lineGfx = new Graphic({
          geometry: polyline,
          symbol: makeLineSymbol(row.lineColor),
          attributes: { _rowObjectId: row.objectId, _type: 'line' },
        });
        gfx.add(lineGfx);

        // Distance label
        const labelPt = getLabelPoint(lg.paths, sr);
        if (labelPt) {
          gfx.add(
            new Graphic({
              geometry: labelPt,
              symbol: makeDistanceLabel(row.distanceMiles ?? 0, row.lineColor),
              attributes: { _rowObjectId: row.objectId, _type: 'label' },
            }),
          );
        }
      }
    },
    [],
  );

  const clearAllLines = useCallback(() => {
    allLinesGfxLayerRef.current?.removeAll();
  }, []);

  // ── Toggle show-all-lines ─────────────────────────────────────────────────
  const toggleShowAllLines = useCallback(() => {
    setShowAllLines((prev) => {
      const next = !prev;
      if (next) {
        drawAllLines(allLineRowsRef.current);
      } else {
        clearAllLines();
      }
      return next;
    });
  }, [drawAllLines, clearAllLines]);

  /** Set the rows to use for "show all lines" mode. Automatically assigns colors. */
  const setAllLineRows = useCallback(
    (rows: NearestPropertyRow[]) => {
      const colored = assignLineColors(rows);
      allLineRowsRef.current = colored;
      // If already showing, redraw with updated rows
      if (showAllLines) {
        drawAllLines(colored);
      }
      return colored;
    },
    [showAllLines, drawAllLines],
  );

  // ── Hover handler ─────────────────────────────────────────────────────────
  const handleHoverRow = useCallback(
    (row: NearestPropertyRow | null) => {
      if (!view || !boundariesLayer) return;

      const hoverGfx = hoverGfxLayerRef.current;
      const allGfx = allLinesGfxLayerRef.current;

      // Clear previous highlight handle
      highlightHandleRef.current?.remove();
      highlightHandleRef.current = null;

      if (!row) {
        // ── Mouse leave ──
        hoverGfx?.removeAll();

        // If "show all lines" mode, restore all lines to normal appearance
        if (showAllLines && allGfx) {
          for (const graphic of allGfx.graphics.toArray()) {
            const objId = graphic.attributes?._rowObjectId;
            const matchRow = allLineRowsRef.current.find((r) => r.objectId === objId);
            if (!matchRow?.lineColor) continue;

            if (graphic.attributes?._type === 'line') {
              graphic.symbol = makeLineSymbol(matchRow.lineColor);
            }
          }
        }
        return;
      }

      // ── Mouse enter: highlight feature via LayerView ──
      const lv = layerViewRef.current;
      if (lv) {
        highlightHandleRef.current = lv.highlight(row.objectId);
      }

      if (showAllLines && allGfx) {
        // In "show all" mode, glow the hovered line and dim others
        for (const graphic of allGfx.graphics.toArray()) {
          const objId = graphic.attributes?._rowObjectId;
          const matchRow = allLineRowsRef.current.find((r) => r.objectId === objId);
          if (!matchRow?.lineColor) continue;

          if (objId === row.objectId) {
            // Glow the hovered line
            if (graphic.attributes?._type === 'line') {
              graphic.symbol = makeGlowSymbol(matchRow.lineColor);
            }
          } else {
            // Dim others
            if (graphic.attributes?._type === 'line') {
              graphic.symbol = makeLineSymbol(matchRow.lineColor, 1.5, 0.3);
            }
          }
        }
        // Don't draw an extra hover line in show-all mode
        return;
      }

      // ── Single hover line mode (default) ──
      if (!hoverGfx) return;
      hoverGfx.removeAll();

      const lg = row.lineGraphic;
      if (!lg) return;

      const sr = SpatialReference.fromJSON(lg.spatialReference);
      const polyline = new Polyline({ paths: lg.paths, spatialReference: sr });

      hoverGfx.add(new Graphic({ geometry: polyline, symbol: DEFAULT_LINE_SYMBOL }));

      const labelPt = getLabelPoint(lg.paths, sr);
      if (labelPt) {
        hoverGfx.add(
          new Graphic({
            geometry: labelPt,
            symbol: makeDistanceLabel(row.distanceMiles ?? 0),
          }),
        );
      }
    },
    [view, boundariesLayer, showAllLines],
  );

  const handleSelectRow = useCallback(
    (row: NearestPropertyRow) => {
      if (!view || !boundariesLayer) return;
      boundariesLayer
        .queryFeatures({
          where: `OBJECTID=${row.objectId}`,
          returnGeometry: true,
          outFields: [],
        })
        .then((result) => {
          const extent = result.features[0]?.geometry?.extent;
          if (extent) view.goTo(extent.expand(1.5), { duration: 400 });
        });
    },
    [view, boundariesLayer],
  );

  return {
    handleHoverRow,
    handleSelectRow,
    /** Whether "show all lines" is currently active */
    showAllLines,
    /** Toggle the "show all lines" state */
    toggleShowAllLines,
    /** Provide the rows that should be drawn when show-all is active. Returns rows with lineColor assigned. */
    setAllLineRows,
    /** The color palette used for line assignment */
    linePalette: LINE_PALETTE,
  };
}
